import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type { SalesMeetingDetail } from "../../lib/sales-types";
import { relativeTime } from "../../lib/time";
import { clearCopy, newerOf, readCopy, writeCopy } from "../../lib/device-copy";
import {
  checklistInsert,
  checklistOf,
  toggleItem,
  UNCHECKED,
  type ChecklistItem,
} from "../../lib/checklist";
import { MessageEditor } from "../editor/MessageEditor";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";

/**
 * A meeting's notes: one document, written like a Google Doc, saved as you go.
 *
 * No Save button. A rep writes these in a car park in three bursts between
 * calls, and a button is a thing to forget to press -- so the editor saves a
 * couple of seconds after typing stops, again when the panel closes, and says
 * which of "Saving", "Saved" and "Not saved" is true underneath.
 *
 * Saving is also what sends the notes to the company: the server rewrites the
 * meeting's entry on the account's CRM timeline on every save, so there is no
 * second step that could be skipped.
 *
 * Checklist items are plain "☐" and "☑" at the start of a bullet -- see
 * `lib/checklist.ts`. The list under the editor is where they are ticked and
 * turned into tasks.
 */

const SAVE_AFTER_MS = 2_000;
/** How often to try again after a save fails -- usually no signal. */
const RETRY_MS = 10_000;

export interface NotesEditorHandle {
  focus: () => void;
}

export const NotesEditor = forwardRef<NotesEditorHandle, { meeting: SalesMeetingDetail }>(
  function NotesEditor({ meeting }, ref) {
    const client = useQueryClient();
    const container = useRef<HTMLDivElement>(null);
    const copyKey = `pulse.meeting-notes.${meeting.id}`;
    /*
     * Start from whatever is newest: the server's notes, or a copy this device
     * kept of an edit that never reached the server -- typed with no signal,
     * and the tab closed before it came back. See `device-copy.ts`.
     */
    const [start] = useState(() =>
      newerOf(
        { value: meeting.notesHtml ?? "", at: meeting.notesUpdatedAt },
        readCopy<string>(copyKey),
        (a, b) => a === b,
      ),
    );
    const [html, setHtml] = useState(start?.value ?? "");
    const restored = start?.fromDevice === true;
    /* Bumped to remount the editor with new content -- after a tick in the
       checklist below, which edits the document from outside the editor. */
    const [editorKey, setEditorKey] = useState(0);
    const saved = useRef(meeting.notesHtml ?? "");
    const [status, setStatus] = useState<"saved" | "dirty" | "saving" | "failed">(
      restored ? "dirty" : "saved",
    );
    const [meta, setMeta] = useState({
      at: meeting.notesUpdatedAt,
      by: meeting.notesUpdatedByName,
    });

    const latest = useRef(html);
    latest.current = html;

    useImperativeHandle(ref, () => ({
      focus: () => {
        const surface = container.current?.querySelector<HTMLElement>("[contenteditable=true]");
        surface?.focus();
      },
    }));

    const save = useMutation({
      mutationFn: (body: string) => salesApi.saveMeetingNotes(meeting.id, body),
      onMutate: () => setStatus("saving"),
      onSuccess: (result, body) => {
        saved.current = body;
        /* The server has it; the device's copy has done its job -- unless
           more was typed while this save was in flight. */
        if (latest.current === body) clearCopy(copyKey);
        setMeta({ at: result.notesUpdatedAt, by: result.notesUpdatedByName });
        setStatus((current) => (current === "saving" ? "saved" : current));
        /* Written into the cached meeting rather than refetched: the follow-up
           composer's "Insert notes" reads it, and a refetch would hand this
           editor a document to fight the caret with. */
        client.setQueryData<SalesMeetingDetail>(queryKeys.salesMeeting(meeting.id), (current) =>
          current
            ? {
                ...current,
                notesHtml: result.hasNotes ? body : null,
                notesUpdatedAt: result.notesUpdatedAt,
                notesUpdatedByName: result.notesUpdatedByName,
              }
            : current,
        );
        /* The feed's bold and the calendar's corner icon both depend on
           whether notes exist; refresh them without refetching this panel,
           which would fight the caret. */
        void client.invalidateQueries({ queryKey: ["sales", "meetings-feed"] });
        void client.invalidateQueries({ queryKey: ["sales", "meetings"] });
        void client.invalidateQueries({ queryKey: queryKeys.salesMeetingCounts });
      },
      onError: () => setStatus("failed"),
    });

    /* `mutate` is stable where the mutation object is not. Depending on the
       object reset the save timer on every render -- including the one a
       failed save causes, which buried "no connection" under "Editing". */
    const { mutate } = save;
    const flush = useCallback(
      (body: string) => {
        if (body === saved.current) return;
        mutate(body);
      },
      [mutate],
    );

    /* Every edit is copied to the device straight away, and saved to the
       server a moment after typing stops. */
    useEffect(() => {
      if (html === saved.current) return;
      writeCopy(copyKey, html);
      setStatus((current) => (current === "saving" ? current : "dirty"));
      const timer = window.setTimeout(() => flush(html), SAVE_AFTER_MS);
      return () => window.clearTimeout(timer);
    }, [html, flush, copyKey]);

    /* No signal: keep trying, quietly. The text is safe on the device. */
    useEffect(() => {
      if (status !== "failed") return;
      const timer = window.setTimeout(() => mutate(latest.current), RETRY_MS);
      return () => window.clearTimeout(timer);
    }, [status, mutate]);

    /* And when the panel closes or moves to another meeting, so nothing typed
       in the last two seconds is lost. A ref, because the cleanup must see the
       latest text rather than the text when it was created. If that save
       fails too, the device's copy is what brings it back next time. */
    useEffect(
      () => () => {
        if (latest.current !== saved.current) {
          const body = latest.current;
          void salesApi
            .saveMeetingNotes(meeting.id, body)
            .then(() => {
              clearCopy(copyKey);
              return client.invalidateQueries({ queryKey: queryKeys.salesRoot });
            })
            .catch(() => undefined);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [meeting.id],
    );

    /* A tab closed mid-sentence: ask, as Google Docs does. */
    useEffect(() => {
      if (status === "saved") return;
      const onUnload = (event: BeforeUnloadEvent): void => {
        event.preventDefault();
      };
      window.addEventListener("beforeunload", onUnload);
      return () => window.removeEventListener("beforeunload", onUnload);
    }, [status]);

    const items = checklistOf(html);

    const replaceDocument = (next: string): void => {
      setHtml(next);
      setEditorKey((key) => key + 1);
    };

    return (
      <div ref={container}>
        <MessageEditor
          key={`${meeting.id}-${editorKey}`}
          initialHtml={html}
          onChange={setHtml}
          uploads={[]}
          onUploadsChange={() => undefined}
          onError={() => undefined}
          allowAttachments={false}
          placeholder="What was said, what they want, what happens next…"
          surfaceHeight="min-h-40 max-h-[60vh]"
          extraTools={(tools) => (
            <button
              type="button"
              title="Checklist item — tick it off, or make it a task"
              aria-label="Checklist item"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => tools.insertHtml(checklistInsert())}
              className="flex h-7 items-center gap-1 rounded px-1.5 text-xs text-muted hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400"
            >
              <span aria-hidden>{UNCHECKED}</span> Checklist
            </button>
          )}
        />

        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-faint">
          <span
            className={cn(
              status === "failed" && "font-medium text-red-600 dark:text-red-400",
            )}
          >
            {status === "saving"
              ? "Saving…"
              : status === "dirty"
                ? "Editing…"
                : status === "failed"
                  ? "No connection — kept on this device, saving when you're back"
                  : meta.at
                    ? `Saved · last edited ${relativeTime(meta.at)}${meta.by ? ` by ${meta.by}` : ""}`
                    : "Saves as you type"}
          </span>
          {restored && status !== "saved" ? (
            <span className="text-amber-700 dark:text-amber-400">
              Put back what you wrote offline.
            </span>
          ) : null}
          {status === "failed" ? (
            <button
              type="button"
              onClick={() => save.mutate(html)}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              Try again
            </button>
          ) : null}
          {/* Right-aligned only where it shares the line. On a phone it
              always wraps, and pushed right under a left-aligned status it
              read as a stagger rather than a second line. */}
          <span className="sm:ml-auto">
            {meeting.companyId
              ? `Filed on ${meeting.crmCompanyName ?? meeting.companyName} as a meeting note`
              : "Not linked to a CRM company, so these stay on the meeting"}
          </span>
        </p>

        {items.length > 0 ? (
          <ActionItems
            meeting={meeting}
            items={items}
            onToggle={(index) => replaceDocument(toggleItem(html, index))}
          />
        ) : null}
      </div>
    );
  },
);

/**
 * The checklist, under the notes: tick things off, or make one a task.
 *
 * A task is the ordinary CRM task -- Lane, the company's timeline, the same
 * assignee rules -- raised through the meeting so it is logged against it. It
 * defaults to you, because an action out of a meeting is nearly always one
 * the person in the room just promised.
 */
function ActionItems({
  meeting,
  items,
  onToggle,
}: {
  meeting: SalesMeetingDetail;
  items: ChecklistItem[];
  onToggle: (index: number) => void;
}) {
  const [making, setMaking] = useState<number | null>(null);
  const raised = new Set(
    meeting.notes.filter((note) => note.kind === "task").map((note) => note.body.trim()),
  );

  return (
    <div className="mt-3 rounded-lg border border-line">
      <p className="border-b border-line px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
        Actions
      </p>
      <ul>
        {items.map((item) => {
          const task = meeting.notes.find(
            (note) => note.kind === "task" && note.body.trim() === item.text,
          );
          return (
            <li key={item.index} className="border-b border-line px-3 py-1.5 last:border-b-0">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={item.done}
                  onChange={() => onToggle(item.index)}
                  aria-label={item.done ? "Mark as not done" : "Mark as done"}
                  className="size-4 shrink-0 accent-brand-600"
                />
                <span className={cn("min-w-0 flex-1 text-sm text-content", item.done && "text-faint line-through")}>
                  {item.text || <em className="text-faint">Empty item</em>}
                </span>
                {task || raised.has(item.text) ? (
                  task?.taskUrl ? (
                    <a
                      href={task.taskUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-[11px] text-brand-600 hover:underline dark:text-brand-400"
                    >
                      Task{task.taskAssignee ? ` · ${task.taskAssignee.split(" ")[0]}` : ""} ↗
                    </a>
                  ) : (
                    <span className="shrink-0 text-[11px] text-faint">Task raised</span>
                  )
                ) : item.text && meeting.companyId ? (
                  <button
                    type="button"
                    onClick={() => setMaking(making === item.index ? null : item.index)}
                    className="shrink-0 rounded-md border border-line px-2 py-0.5 text-[11px] text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    Make task
                  </button>
                ) : null}
              </div>
              {making === item.index && meeting.companyId ? (
                <TaskForm meeting={meeting} title={item.text} onDone={() => setMaking(null)} />
              ) : null}
            </li>
          );
        })}
      </ul>
      {!meeting.companyId ? (
        <p className="border-t border-line px-3 py-1.5 text-[11px] text-faint">
          Link this meeting to a CRM company to turn these into tasks.
        </p>
      ) : null}
    </div>
  );
}

function TaskForm({
  meeting,
  title,
  onDone,
}: {
  meeting: SalesMeetingDetail;
  title: string;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null);
  const [due, setDue] = useState("");

  const boards = useQuery({
    queryKey: queryKeys.crmLaneBoards(meeting.companyId ?? undefined),
    queryFn: () => crmApi.laneBoards(meeting.companyId ?? undefined),
    staleTime: 5 * 60_000,
  });

  const self = boards.data?.selfAssigneeId ?? "";
  const owner = picked ?? self;

  const create = useMutation({
    mutationFn: () =>
      salesApi.addMeetingTask(meeting.id, {
        title,
        assigneeIds: owner ? [owner] : undefined,
        /* The end of the working day it is due, so it is not overdue at nine. */
        dueAt: due ? new Date(`${due}T17:00:00`).toISOString() : null,
      }),
    onSuccess: (detail) => {
      client.setQueryData(queryKeys.salesMeeting(meeting.id), detail);
      onDone();
    },
  });

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 pl-6 text-xs">
      <label className="flex items-center gap-1 text-muted">
        Owner
        <select
          value={owner}
          onChange={(event) => setPicked(event.target.value)}
          className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
        >
          {self ? <option value={self}>You</option> : <option value="">Account manager</option>}
          {(boards.data?.assignees ?? [])
            .filter((person) => person.id !== self)
            .map((person) => (
              <option key={person.id} value={person.id}>
                {person.name ?? person.email}
              </option>
            ))}
        </select>
      </label>
      <label className="flex items-center gap-1 text-muted">
        Due
        <input
          type="date"
          value={due}
          onChange={(event) => setDue(event.target.value)}
          className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
        />
      </label>
      <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
        {create.isPending ? "Creating…" : "Create task"}
      </Button>
      {create.isError ? (
        <span className="text-red-600">
          {create.error instanceof ApiError ? create.error.message : "Could not create that task."}
        </span>
      ) : null}
    </div>
  );
}
